/**
 * SPEAKON chat interface.
 *
 * French-first, responsive, and honest about backend state: the header shows
 * whether Hermes is actually reachable, and progress text always comes from real
 * API responses rather than simulated timers.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import type { AiProductDraft, AiIdentity } from "../../api/ai/types";
import {
  Sparkles,
  Send,
  ImagePlus,
  X,
  Loader2,
  Bot,
  User,
  Package,
  AlertTriangle,
  CheckCircle2,
  PackagePlus,
  Wand2,
  BarChart3,
  Target,
  Settings2,
} from "lucide-react";
import toast from "react-hot-toast";
import aiApi from "../../api/ai/get";

const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
const MAX_IMAGES = 6;

/* ------------------------------------------------------------------ types */

type ChatRole = "user" | "assistant";

interface ChatMessage {
  id: string;
  role: ChatRole;
  content: string;
  images?: string[];
  isError?: boolean;
  degraded?: boolean;
}

interface DuplicateCandidate {
  productId: string;
  name: string;
  quantityInStock: number;
}

interface DraftResult {
  ok?: boolean;
  stage?: string;
  ready?: boolean;
  missing?: string[];
  message?: string;
  needsClarification?: boolean;
  needsStoreSelection?: boolean;
  duplicateSuspected?: boolean;
  cost?: {
    model: string;
    quality: string;
    unitCostFcfa: number;
    totalCostFcfa: number;
    generatedCount: number;
    modelDeprecated?: boolean;
  };
  stores?: { storeId: string; name: string; city?: string }[];
  analysis?: { uncertain?: string[] };
  draft?: {
    name: string;
    brand?: string;
    category?: string;
    quantity?: number | null;
    sellPrice?: number | null;
    buyPrice?: number | null;
    store?: { name: string; city?: string };
    duplicateCandidates?: DuplicateCandidate[];
    sourceImages?: string[];
    imageVariants?: ImageVariant[];
    selectedVariantId?: string | null;
  };
}

interface PriceTier {
  quality: string;
  costUsd: number;
  costFcfa: number;
}

interface PriceEntry {
  model: string;
  label: string;
  deprecated?: boolean;
  recommended?: boolean;
  budget?: boolean;
  qualities: PriceTier[];
}

interface ImageStudioSettings {
  model: string;
  quality: string;
  styles: { id: string; label: string; hint: string }[];
  prices: PriceEntry[];
}

interface ImageVariant {
  id: string;
  styleId: string;
  label: string;
  hint?: string;
  imageUrl: string;
  imageKey?: string;
  imagePreviewUrl?: string;
  isOriginal: boolean;
  index?: number;
  model?: string;
  quality?: string;
  costFcfa?: number;
}

interface AiHealth {
  integrations?: {
    hermes?: { reachable?: boolean; enabled?: boolean };
    mcp?: { credentialMode?: "user-store"; credentialsStoredEncrypted?: boolean };
    search?: { configured?: boolean };
    llm?: { defaultProvider?: string };
  };
  imageStudio?: ImageStudioSettings;
}

interface UserDataState {
  UserReducer?: {
    userdata?: {
      user?: { _id?: string };
      store?: { _id?: string };
    };
  };
}

const QUICK_ACTIONS: { id: string; label: string; icon: typeof Package }[] = [
  { id: "add_product", label: "Ajouter un produit avec une photo", icon: ImagePlus },
  { id: "find_customers", label: "Trouver des clients", icon: Target },
  { id: "promote", label: "Promouvoir mes produits", icon: Wand2 },
  { id: "sales", label: "Analyser mes ventes", icon: BarChart3 },
  { id: "stock", label: "Gérer mes stocks", icon: Package },
];

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Lecture du fichier impossible"));
    reader.readAsDataURL(file);
  });

/** Money, French formatting: 18000 -> "18 000". */
const formatFcfa = (value: number | null | undefined) =>
  Number(value || 0).toLocaleString("fr-FR");

const errorMessage = (error: unknown, fallback: string): string => {
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string" && message) return message;
  }
  return fallback;
};

/* ------------------------------------------------------- product draft card */

interface DraftCardProps {
  result: DraftResult;
  busy: boolean;
  onConfirm: () => void;
  onDiscard: () => void;
  onAddToExisting: (candidate: DuplicateCandidate) => void;
  onGenerateVariants: () => void;
  onSelectVariant: (variantId: string) => void;
  onQualityChange: (quality: string) => void;
  selectedQuality: string;
  variantsBusy: boolean;
  studio: ImageStudioSettings | null;
}

function DraftCard({
  result,
  busy,
  onConfirm,
  onDiscard,
  onAddToExisting,
  onGenerateVariants,
  onSelectVariant,
  onQualityChange,
  selectedQuality,
  variantsBusy,
  studio,
}: DraftCardProps) {
  if (result.needsStoreSelection) {
    return (
      <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
        <p className="text-sm font-semibold text-amber-900">
          {result.message ?? "Plusieurs boutiques correspondent."}
        </p>
        <ul className="mt-2 space-y-1">
          {(result.stores ?? []).map((store) => (
            <li key={store.storeId} className="text-sm text-amber-800">
              • {store.name}
              {store.city ? ` — ${store.city}` : ""}
            </li>
          ))}
        </ul>
        <p className="mt-2 text-xs text-amber-700">
          Indiquez la boutique à utiliser dans votre message.
        </p>
      </div>
    );
  }

  if (result.needsClarification) {
    const uncertain = result.analysis?.uncertain ?? [];
    return (
      <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
        <p className="text-sm font-semibold text-amber-900">
          {result.message ?? "Je ne suis pas certain de l'identification."}
        </p>
        {uncertain.length > 0 && (
          <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-amber-800">
            {uncertain.slice(0, 4).map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  const draft = result.draft;
  if (!draft) return null;

  const canConfirm = Boolean(result.ready) && !result.duplicateSuspected;

  const variants: ImageVariant[] = draft.imageVariants ?? [];

  // Cost of the NEXT render, taken from the live price ladder for this quality.
  const activePrice = studio?.prices?.find((entry) => entry.model === studio.model)
  const unitCostFcfa =
    activePrice?.qualities?.find((tier) => tier.quality === selectedQuality)?.costFcfa ??
    0
  const modelDeprecated = Boolean(activePrice?.deprecated)

  return (
    <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 bg-slate-50 px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Fiche produit proposée
        </p>
      </div>

      <dl className="divide-y divide-slate-100 px-4 text-sm">
        <Row label="Produit" value={draft.name} />
        <Row
          label="Marque"
          value={draft.brand || undefined}
          fallback="Non confirmée"
        />
        <Row
          label="Catégorie"
          value={draft.category || undefined}
          fallback="À définir"
          fallbackClass="text-amber-600"
        />
        <Row
          label="Boutique"
          value={
            draft.store
              ? `${draft.store.name}${draft.store.city ? ` — ${draft.store.city}` : ""}`
              : undefined
          }
        />
        <Row
          label="Quantité"
          value={draft.quantity ?? undefined}
          fallback="Non précisée"
          fallbackClass="text-amber-600"
        />
        <Row
          label="Prix de vente"
          value={
            draft.sellPrice !== null && draft.sellPrice !== undefined
              ? `${Number(draft.sellPrice).toLocaleString("fr-FR")} FCFA`
              : undefined
          }
          fallback="Non précisé"
          fallbackClass="text-amber-600"
        />
      </dl>

      {result.duplicateSuspected && (
        <div className="mx-4 mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600" />
          <div className="text-sm text-amber-900">
            <p className="font-semibold">Produit déjà présent ?</p>
            <ul className="mt-1 space-y-0.5">
              {(draft.duplicateCandidates ?? []).map((candidate) => (
                <li key={candidate.productId}>
                  • {candidate.name} — {candidate.quantityInStock} unité(s) en stock
                </li>
              ))}
            </ul>
            <p className="mt-1">
              Préférez ajouter du stock au produit existant plutôt que d'en créer un second.
            </p>
          </div>
        </div>
      )}
      {(result.missing?.length ?? 0) > 0 && (
        <div className="mx-4 mb-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
          Il me manque : {(result.missing ?? []).join(", ")}.
        </div>
      )}

      {/* -------------------------------------------------- image variants */}
      <div className="mx-4 mb-3 rounded-xl border border-slate-200 bg-slate-50/70 p-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Image du produit
          </p>
          <button
            type="button"
            onClick={onGenerateVariants}
            disabled={variantsBusy || busy}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-cyan-400 hover:text-cyan-800 disabled:cursor-not-allowed disabled:opacity-50"
            title="Génère des versions améliorées du visuel (fond et ambiance)"
          >
            {variantsBusy ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <Wand2 size={13} />
            )}
            {variantsBusy ? "Génération…" : "Améliorer l'image"}
          </button>
        </div>

        {/* Quality / cost control. Always show the price so a merchant is never
            surprised by what a render costs. */}
        {studio && studio.prices.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <label className="flex items-center gap-1.5 text-[11px] font-medium text-slate-600">
              Qualité
              <select
                value={selectedQuality}
                onChange={(event) => onQualityChange(event.target.value)}
                disabled={variantsBusy || busy}
                className="rounded-md border border-slate-300 bg-white px-1.5 py-0.5 text-[11px] text-slate-800 outline-none focus:border-cyan-400"
              >
                <option value="low">Rapide</option>
                <option value="medium">Standard</option>
                <option value="high">Premium</option>
              </select>
            </label>

            <span className="text-[11px] text-slate-500">
              ≈&nbsp;
              <span className="font-semibold text-slate-700">
                {formatFcfa(unitCostFcfa)} FCFA
              </span>
              &nbsp;par rendu
            </span>

            <span className="text-[11px] text-slate-400">
              {studio.model}
              {modelDeprecated ? " — modèle retiré" : ""}
            </span>
          </div>
        )}

        {variants.length > 0 ? (
          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
            {variants.map((variant) => {
              const selected = draft.selectedVariantId === variant.id;
              return (
                <button
                  key={variant.id}
                  type="button"
                  onClick={() => onSelectVariant(variant.id)}
                  title={variant.hint || variant.label}
                  className={`group relative overflow-hidden rounded-lg border-2 transition ${
                    selected ? "border-cyan-500 ring-2 ring-cyan-200" : "border-slate-200 hover:border-cyan-300"
                  }`}
                >
                  <img
                    src={variant.imagePreviewUrl || variant.imageUrl}
                    alt={variant.label}
                    loading="lazy"
                    className="aspect-square w-full bg-white object-cover"
                  />
                  <span className="absolute inset-x-0 bottom-0 bg-slate-900/80 px-2 py-1 text-[10px] font-semibold text-white">
                    {variant.label}
                  </span>
                  {variant.isOriginal && (
                    <span className="absolute left-1 top-1 rounded bg-slate-900/70 px-1.5 py-0.5 text-[9px] font-bold uppercase text-white">
                      originale
                    </span>
                  )}
                  {selected && (
                    <span className="absolute right-1 top-1 rounded-full bg-cyan-500 p-1 text-white">
                      <CheckCircle2 size={11} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        ) : (
          <p className="mt-2 text-xs text-slate-500">
            Ajoutez une photo, puis choisissez « Améliorer l'image » pour obtenir plusieurs
            rendus professionnels. La photo originale reste toujours disponible.
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-2 border-t border-slate-100 bg-slate-50 px-4 py-3">
        {result.duplicateSuspected ? (
          /* A duplicate is likely: offer to top up the existing product instead of
             silently creating a second one. */
          (draft.duplicateCandidates ?? []).map((candidate) => (
            <button
              key={candidate.productId}
              type="button"
              onClick={() => onAddToExisting(candidate)}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <PackagePlus size={16} />
              )}
              Ajouter {draft.quantity ?? ""} unité(s) à « {candidate.name} »
            </button>
          ))
        ) : (
          <button
            type="button"
            onClick={onConfirm}
            disabled={!canConfirm || busy}
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <CheckCircle2 size={16} />
            )}
            Confirmer l'ajout
          </button>
        )}
        <button
          type="button"
          onClick={onDiscard}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
        >
          <X size={16} />
          Annuler
        </button>
      </div>
    </div>
  );
}

interface RowProps {
  label: string;
  value?: string | number;
  fallback?: string;
  fallbackClass?: string;
}

function Row({ label, value, fallback, fallbackClass = "text-slate-400" }: RowProps) {
  return (
    <div className="flex justify-between gap-4 py-2">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-right font-medium text-slate-900">
        {value !== undefined && value !== null && value !== "" ? (
          value
        ) : (
          <span className={fallbackClass}>{fallback ?? "—"}</span>
        )}
      </dd>
    </div>
  );
}

/* -------------------------------------------------------------- main view */

interface GestockAiChatProps {
  /** Called when a long-running operation finishes, so the floating button can
   *  surface a notification if the modal is closed. */
  onBackgroundResult?: (result: { title: string; message: string }) => void;
  /** Tracks whether agent work is still running, for the button badge. */
  onPendingChange?: (pending: boolean) => void;
  /** The full-page route renders its own header, so hide the inline status. */
  hideStatusBadge?: boolean;
}

export default function GestockAiChat(props: GestockAiChatProps) {
  const userdata = useSelector((state: UserDataState) => state.UserReducer?.userdata);
  return <ScopedGestockAiChat key={`${userdata?.user?._id}:${userdata?.store?._id}`} {...props} />;
}

function ScopedGestockAiChat({
  onBackgroundResult,
  onPendingChange,
  hideStatusBadge = false,
}: GestockAiChatProps) {
  const userdata = useSelector(
    (state: UserDataState) => state.UserReducer?.userdata
  );
  const storeId: string | undefined = userdata?.store?._id;

  // The conversation survives a close AND a page reload, so reopening always
  // lands on the same session rather than starting a new one.
  const SESSION_KEY = `gestock:ai:${userdata?.user?._id}:${storeId}:conversationId`
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [images, setImages] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [draftResult, setDraftResult] = useState<DraftResult | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(SESSION_KEY);
    } catch {
      return null;
    }
  });
  const [identity, setIdentity] = useState<AiIdentity | null>(null);
  const [identityError, setIdentityError] = useState<string | null>(null);
  const [health, setHealth] = useState<AiHealth | null>(null);
  const [showStatus, setShowStatus] = useState(false);
  const [agentMode, setAgentMode] = useState<string | null>(null);

  const rememberConversation = useCallback((id: string | null) => {
    setConversationId(id);
    try {
      if (id) localStorage.setItem(SESSION_KEY, id);
      else localStorage.removeItem(SESSION_KEY);
    } catch {
      /* storage unavailable — session simply won't survive a reload */
    }
  }, [SESSION_KEY]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, draftResult]);

  useEffect(() => {
    aiApi
      .health()
      .then((data: AiHealth) => setHealth(data))
      .catch(() => setHealth({ integrations: {} }));
  }, []);

  useEffect(() => {
    if (!storeId) return;
    let alive = true;
    aiApi.identity(storeId)
      .then((data: AiIdentity) => { if (alive) setIdentity(data); })
      .catch((error: unknown) => { if (alive) setIdentityError(errorMessage(error, "Connexion privée indisponible.")); });
    return () => { alive = false; };
  }, [storeId]);

  /*
   * Restore the previous conversation when the assistant reopens.
   *
   * The overlay unmounts the chat on close, so in-memory messages would be lost —
   * and a page reload loses them regardless. Rebuild the thread from the server.
   */
  const [historyLoaded, setHistoryLoaded] = useState(false);

  useEffect(() => {
    if (!conversationId || historyLoaded) return;

    /*
     * `alive` guards our own writes, but is deliberately NOT set when we bail out
     * early. Setting state from a cancelled pass would flip `historyLoaded`,
     * re-run the effect and cancel the still-pending retry (React StrictMode
     * mounts every effect twice in development), so the second fetch never lands.
     */
    let alive = true;

    (async () => {
      try {
        const stored = await aiApi.conversationMessages(String(conversationId));
        if (!alive) return;

        if (Array.isArray(stored) && stored.length > 0) {
          setMessages(
            stored
              .filter((entry: { role?: string }) => entry?.role === "user" || entry?.role === "assistant")
              .map((entry: { role?: string; content?: string; images?: string[] }, index: number) => ({
                id: `h-${conversationId}-${index}`,
                role: entry.role === "user" ? "user" : "assistant",
                content: String(entry.content ?? ""),
                images: Array.isArray(entry.images) ? entry.images.slice(0, 6) : [],
                isError: false,
              }))
          );
        }
      } catch {
        // History is a convenience; if it fails the thread simply starts empty.
      } finally {
        if (alive) setHistoryLoaded(true);
      }
    })();

    return () => {
      alive = false;
    };
  }, [conversationId, historyLoaded]);

  const pushMessage = useCallback((message: ChatMessage) => {
    setMessages((current) => [...current, message]);
  }, []);

  const handleFiles = useCallback(async (files: FileList | null) => {
    const selected = Array.from(files ?? []).filter((file): file is File => Boolean(file));
    if (!selected.length) return;

    const accepted: string[] = [];
    for (const file of selected) {
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} n'est pas une image.`);
        continue;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        toast.error(`${file.name} dépasse 8 Mo.`);
        continue;
      }
      try {
        accepted.push(await readFileAsDataUrl(file));
      } catch {
        toast.error(`Lecture de ${file.name} impossible.`);
      }
    }

    if (accepted.length) {
      setImages((current) => [...current, ...accepted].slice(0, MAX_IMAGES));
    }
  }, []);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if ((!text && images.length === 0) || busy) return;

    const attachments = images;
    setInput("");
    setImages([]);
    setBusy(true);
    onPendingChange?.(true);
    setDraftResult(null);

    pushMessage({
      id: `u-${Date.now()}`,
      role: "user",
      content: text,
      images: attachments,
    });

    try {
      const response = await aiApi.chat({
        message: text || "Analyse ces images.",
        images: attachments,
        conversationId: conversationId ?? undefined,
        storeId,
      });

      if (response?.conversationId) rememberConversation(response.conversationId);
      if (response?.agent) setAgentMode(String(response.agent));

      // An image request returns a reviewable product draft — show the card so the
      // merchant can confirm it, exactly like the dedicated photo flow.
      if (response?.draft) {
        setDraftResult({
          ok: response.ok,
          stage: response.stage,
          ready: response.ready,
          missing: response.missing,
          message: response.message,
          needsClarification: response.needsClarification,
          needsStoreSelection: response.needsStoreSelection,
          duplicateSuspected: response.duplicateSuspected,
          draft: response.draft,
        } as AiProductDraft);
      }

      pushMessage({
        id: `a-${Date.now()}`,
        role: "assistant",
        content: String(response?.message ?? ""),
        degraded: response?.degraded === true,
      });

      // The run finished: tell the launcher so it can notify if the modal is closed.
      if (String(response?.message ?? "").trim()) {
        onBackgroundResult?.({ title: "Nouvelle réponse SPEAKON", message: String(response.message) });
      }
    } catch (error) {
      const detail = errorMessage(error, "L'assistant est indisponible.");
      pushMessage({ id: `e-${Date.now()}`, role: "assistant", content: detail, isError: true });
      toast.error(detail);
    } finally {
      setBusy(false);
      onPendingChange?.(false);
    }
  }, [input, images, busy, conversationId, storeId, pushMessage, rememberConversation, onBackgroundResult, onPendingChange]);

  const handleAddProduct = useCallback(async () => {
    if (images.length === 0) {
      toast("Ajoutez au moins une photo du produit.", { icon: "📷" });
      fileInputRef.current?.click();
      return;
    }
    if (busy) return;

    setBusy(true);
    setDraftResult(null);

    try {
      const result = (await aiApi.buildDraft({
        images,
        hint: input.trim(),
        quantity: undefined,
        sellPrice: undefined,
        buyPrice: undefined,
        storeHint: undefined,
        conversationId: conversationId ?? undefined,
      })) as AiProductDraft;

      setDraftResult(result);

      if (result?.message) {
        pushMessage({ id: `d-${Date.now()}`, role: "assistant", content: result.message });
        if (result.stage === "READY_TO_CONFIRM") {
          onBackgroundResult?.({ title: "Fiche produit prête", message: result.message });
        }
      }
    } catch (error) {
      const detail = errorMessage(error, "Analyse impossible.");
      pushMessage({ id: `e-${Date.now()}`, role: "assistant", content: detail, isError: true });
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  }, [images, input, busy, conversationId, pushMessage, onBackgroundResult]);

  const handleConfirmDraft = useCallback(async () => {
    if (!draftResult?.draft) return;
    setBusy(true);
    try {
      // Never send the base64 photos again: the draft already holds their S3
      // descriptors, and re-sending them overflows the request body.
      const result = await aiApi.confirmDraft({
        draft: draftResult.draft,
        approved: true,
        conversationId: conversationId ?? undefined,
      });
      const doneMessage = String(result?.message ?? "Produit ajouté.");
      toast.success(doneMessage);
      pushMessage({ id: `ok-${Date.now()}`, role: "assistant", content: doneMessage });
      onBackgroundResult?.({ title: "Produit enregistré", message: doneMessage });
      setDraftResult(null);
      setImages([]);
    } catch (error) {
      toast.error(errorMessage(error, "Enregistrement impossible."));
    } finally {
      setBusy(false);
    }
  }, [draftResult, conversationId, pushMessage, onBackgroundResult]);

  /**
   * The draft matched an existing product. Rather than creating a second one,
   * top up the existing product's stock by the quantity from the draft.
   */
  const handleAddToExisting = useCallback(
    async (candidate: DuplicateCandidate) => {
      if (!candidate?.productId) return;
      const delta = draftResult?.draft?.quantity;

      if (!delta || Number(delta) <= 0) {
        toast.error("Quantité non précisée : indiquez combien d'unités ajouter.");
        return;
      }

      setBusy(true);
      try {
        const result = await aiApi.adjustStock({
          productId: candidate.productId,
          delta: Number(delta),
          buyPrice: draftResult?.draft?.buyPrice ?? undefined,
          reason: "Réapprovisionnement proposé par SPEAKON",
        });

        const message =
          String(
            result?.summary ||
              `Stock de « ${candidate.name} » mis à jour (${candidate.quantityInStock} → ${result?.quantityAfter ?? "?"}).`
          );

        toast.success(message);
        pushMessage({ id: `adj-${Date.now()}`, role: "assistant", content: message });
        setDraftResult(null);
        setImages([]);
      } catch (error) {
        toast.error(errorMessage(error, "Ajustement de stock impossible."));
      } finally {
        setBusy(false);
      }
    },
    [draftResult, pushMessage]
  );

  /**
   * Kick off AI image variants for the current draft. Generation takes ~35s per
   * variant, so it runs server-side and we poll the task until it finishes.
   */
  const [variantsBusy, setVariantsBusy] = useState(false)
  const [selectedQuality, setSelectedQuality] = useState("low")

  const studio = health?.imageStudio ?? null
  // Keep the selector in sync with whatever the backend is configured to use.
  useEffect(() => {
    if (health?.imageStudio?.quality) setSelectedQuality(health.imageStudio.quality);
  }, [health?.imageStudio?.quality]);

  const handleGenerateVariants = useCallback(async () => {
    const sources = draftResult?.draft?.sourceImages?.length
      ? draftResult.draft.sourceImages
      : images.length
        ? images
        : []

    if (!sources.length) {
      toast("Ajoutez d'abord une photo du produit.", { icon: "📷" });
      return;
    }

    setVariantsBusy(true);
    onPendingChange?.(true);
    try {
      const { taskId } = await aiApi.requestImageVariants({
        images: sources.slice(0, 3),
        styleIds: (studio?.styles ?? []).map((style) => style.id).slice(0, 4),
        model: studio?.model,
        quality: selectedQuality,
      });

      if (!taskId) throw new Error("Aucune tâche créée");

      pushMessage({
        id: `gen-${Date.now()}`,
        role: "assistant",
        content: "Je prépare plusieurs rendus de cette photo. Cela prend environ 2 minutes…",
      });

      // Poll for real progress rather than faking it.
      const poll = async (): Promise<ImageVariant[]> => {
        for (let i = 0; i < 60; i += 1) {
          await new Promise((resolve) => setTimeout(resolve, 3000));
          const task = await aiApi.task(String(taskId));
          if (task?.status === "SUCCEEDED") return task?.result?.variants ?? [];
          if (task?.status === "FAILED" || task?.status === "CANCELLED") {
            throw new Error(task?.error?.message || "Génération échouée");
          }
        }
        throw new Error("Délai dépassé pour la génération des rendus.");
      };

      let variants = await poll();

      // Presigned urls expire after 15 min — re-mint so the thumbnails stay alive.
      try {
        const fresh = await aiApi.previewImageVariants(String(taskId));
        if (Array.isArray(fresh?.variants) && fresh.variants.length) variants = fresh.variants;
      } catch {
        // Non-fatal: the urls minted at generation time still work for a while.
      }

      setDraftResult((current) =>
        current?.draft
          ? {
              ...current,
              draft: {
                ...current.draft,
                imageVariants: variants,
                selectedVariantId: variants.find((v) => v.isOriginal)?.id ?? variants[0]?.id ?? null,
              },
            }
          : current
      );

      const generated = variants.filter((variant) => !variant.isOriginal).length;
      const doneMessage = generated
        ? `${variants.length} visuels sont disponibles (${generated} rendu(s) amélioré(s)). Choisissez celui à conserver.`
        : "Aucun rendu n'a pu être généré. La photo originale reste utilisable.";

      pushMessage({ id: `done-${Date.now()}`, role: "assistant", content: doneMessage });
      onBackgroundResult?.({ title: "Visuels du produit prêts", message: doneMessage });
    } catch (error) {
      const detail = errorMessage(error, "Génération des rendus impossible.");
      pushMessage({ id: `gen-err-${Date.now()}`, role: "assistant", content: detail, isError: true });
      toast.error(detail);
    } finally {
      setVariantsBusy(false);
      onPendingChange?.(false);
    }
  }, [draftResult, images, pushMessage, studio, selectedQuality, onBackgroundResult, onPendingChange]);

  const handleSelectVariant = useCallback((variantId: string) => {
    setDraftResult((current) =>
      current?.draft
        ? { ...current, draft: { ...current.draft, selectedVariantId: variantId } }
        : current
    );
  }, []);

  const hermesReady = useMemo(
    () => Boolean(identity?.hermesConnected),
    [identity]
  );

  // One word, per design: "Connecté" / "Hors ligne".
  const statusLine = useMemo(() => {
    if (!health || (!identity && !identityError)) return "Connexion…";
    if (hermesReady) return "Connecté";
    if (health?.integrations?.hermes?.enabled) return "Injoignable";
    return "Hors ligne";
  }, [health, hermesReady, identity, identityError]);

  const statusTitle = hermesReady
    ? `Votre connexion privée est active pour ${identity?.storeName || "cette boutique"}.`
    : "Hermes Agent n'est pas joignable : l'assistant peut répondre mais pas encore exécuter d'action.";

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <header className="flex items-center justify-between gap-3 border-b border-slate-200 bg-gradient-to-r from-slate-900 to-slate-800 px-5 py-4">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan-400/20 text-cyan-300">
            <Sparkles size={20} />
          </span>
          <div>
            <h2 className="text-base font-semibold text-white">SPEAKON</h2>
            <p className="text-xs text-slate-300">
              Votre assistant intelligent pour gérer et développer votre activité.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!hideStatusBadge && (
            <span
              title={statusTitle}
              className={`hidden rounded-full px-3 py-1 text-xs font-medium sm:inline-block ${
                hermesReady
                  ? "bg-emerald-500/20 text-emerald-300"
                  : "bg-amber-500/20 text-amber-300"
              }`}
            >
              {statusLine}
            </span>
          )}
          <button
            type="button"
            onClick={() => setShowStatus((value) => !value)}
            className="rounded-lg p-2 text-slate-300 transition hover:bg-white/10 hover:text-white"
            aria-label="État des intégrations"
          >
            <Settings2 size={18} />
          </button>
        </div>
      </header>

      <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-5 py-2 text-xs text-slate-600" role="status">
        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${hermesReady ? "bg-emerald-500" : "bg-amber-400"}`} />
        {identity ? `Espace IA privé · ${identity.storeName}` : identityError || "Préparation de votre espace IA privé…"}
      </div>

      {showStatus && (
        <div className="border-b border-slate-200 bg-slate-50 px-5 py-4 text-sm text-slate-600">
          <p className="font-semibold text-slate-800">État des intégrations</p>
          <ul className="mt-2 space-y-1">
            <li>
              • Hermes Agent :{" "}
              {hermesReady ? "connexion privée active" : "connexion privée indisponible"}
            </li>
            <li>
              • Outils MCP :{" "}
              {identity?.status === "ready" ? "liés à votre compte et à cette boutique" : "connexion en préparation"}
            </li>
            <li>
              • Recherche web :{" "}
              {health?.integrations?.search?.configured ? "configurée" : "non configurée"}
            </li>
            <li>• Fournisseur de modèles : {health?.integrations?.llm?.defaultProvider ?? "—"}</li>
          </ul>
          {agentMode && (
            <p className="mt-2 text-xs text-slate-500">
              Mode actif :{" "}
              {agentMode === "HERMES"
                ? "Hermes (outils activés)"
                : "LLM direct (lecture seule)"}
            </p>
          )}
        </div>
      )}

      <div className="flex-1 space-y-4 overflow-y-auto bg-slate-50/60 px-5 py-6">
        {messages.length === 0 && (
          <div className="mx-auto max-w-lg text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 text-white">
              <Sparkles size={26} />
            </span>
            <h3 className="mt-4 text-lg font-semibold text-slate-900">Bonjour !</h3>
            <p className="mt-1 text-sm text-slate-600">
              Comment puis-je vous aider à développer votre activité aujourd'hui ?
            </p>
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex gap-3 ${
              message.role === "user" ? "justify-end" : "justify-start"
            }`}
          >
            {message.role === "assistant" && (
              <span className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-900 text-white">
                <Bot size={16} />
              </span>
            )}

            <div
              className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                message.role === "user"
                  ? "bg-slate-900 text-white"
                  : message.isError
                    ? "border border-red-200 bg-red-50 text-red-800"
                    : "border border-slate-200 bg-white text-slate-800"
              }`}
            >
              {message.content}

              {message.degraded && (
                <p className="mt-2 rounded-lg bg-amber-50 px-2 py-1 text-xs text-amber-800">
                  Hermes n'est pas connecté : je peux expliquer mais pas encore exécuter d'action.
                </p>
              )}

              {message.images && message.images.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {message.images.map((image, index) => (
                    <img
                      key={index}
                      src={image}
                      alt={`Pièce jointe ${index + 1}`}
                      className="h-20 w-20 rounded-lg object-cover"
                    />
                  ))}
                </div>
              )}
            </div>

            {message.role === "user" && (
              <span className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-200 text-slate-700">
                <User size={16} />
              </span>
            )}
          </div>
        ))}

        {busy && (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 size={16} className="animate-spin" />
            SPEAKON réfléchit…
          </div>
        )}

        {draftResult && !busy && (
          <DraftCard
            result={draftResult}
            busy={busy}
            onConfirm={handleConfirmDraft}
            onDiscard={() => setDraftResult(null)}
            onAddToExisting={handleAddToExisting}
            onGenerateVariants={() => void handleGenerateVariants()}
            onSelectVariant={handleSelectVariant}
            onQualityChange={setSelectedQuality}
            selectedQuality={selectedQuality}
            variantsBusy={variantsBusy}
            studio={studio}
          />
        )}

        <div ref={bottomRef} />
      </div>

      <div className="border-t border-slate-200 bg-white px-5 py-3">
        <div className="flex flex-wrap gap-2">
          {QUICK_ACTIONS.map((action) => {
            const Icon = action.icon;
            const isProductAction = action.id === "add_product";
            return (
              <button
                key={action.id}
                type="button"
                onClick={
                  isProductAction ? handleAddProduct : () => setInput(action.label)
                }
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:border-cyan-400 hover:bg-cyan-50 hover:text-cyan-800"
              >
                <Icon size={14} />
                {action.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="border-t border-slate-200 bg-white px-5 py-4">
        {images.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-2">
            {images.map((image, index) => (
              <div key={index} className="relative">
                <img src={image} alt="" className="h-16 w-16 rounded-lg object-cover" />
                <button
                  type="button"
                  onClick={() =>
                    setImages((current) => current.filter((_, i) => i !== index))
                  }
                  className="absolute -right-1.5 -top-1.5 rounded-full bg-slate-900 p-0.5 text-white"
                  aria-label="Retirer l'image"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-end gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(event) => {
              void handleFiles(event.target.files);
              event.target.value = "";
            }}
          />

          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="rounded-xl border border-slate-200 p-2.5 text-slate-600 transition hover:bg-slate-50 hover:text-cyan-700"
            aria-label="Ajouter une image"
          >
            <ImagePlus size={20} />
          </button>

          <textarea
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                void sendMessage();
              }
            }}
            rows={1}
            placeholder="Posez votre question, ou décrivez le produit à ajouter…"
            className="flex-1 resize-none rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none transition focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100"
          />

          <button
            type="button"
            onClick={() => void sendMessage()}
            disabled={busy || (!input.trim() && images.length === 0)}
            className="rounded-xl bg-cyan-600 p-2.5 text-white transition hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Envoyer"
          >
            {busy ? (
              <Loader2 size={20} className="animate-spin" />
            ) : (
              <Send size={20} />
            )}
          </button>
        </div>

        <p className="mt-2 text-xs text-slate-400">
          Les actions sont soumises à votre politique d'approbation. GESTOCK ne partage jamais vos
          données avec une autre boutique.
        </p>
      </div>
    </div>
  );
}